import { AltoSpinningLoader, Body, Icon, Row, Tag } from '@alto/design-system';
import { ORDER_TYPE_CONTEXT, OrderTypeSelectionSection } from '@alto/features';
import React from 'react';
import styled from 'styled-components';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { getPaymentString } from '~shared/features/cart/selectors/getPaymentString';
import { getMedicationForPrescription } from '~shared/features/my-meds/selectors/getMedicationsFromPrescriptions';
import { getPrescriptionByID } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { getItemPricingTotal } from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { formatQuantity } from '~shared/helpers/helper';
import { useOrderPricing } from '~shared/queries';
import { useSelectorShared } from '~shared/store';
import { UserTag } from '~web/features/family-accounts/components/UserTag';

type Props = {
  readonly prescriptionID: number;
  readonly includeNewItemTag?: boolean;
  readonly earliestUpcomingShipmentID?: number;
};
export const MedContainer = styled.div`
  flex: 2;
  > div {
    height: auto;
  }
`;

export const MedSummaryRow = ({ earliestUpcomingShipmentID, prescriptionID, includeNewItemTag = false }: Props) => {
  // when order bundling show price for already bundled prescription ids from orderPricing of earliest upcoming shipment
  // otherwise show price for new items bundled into order from orderPricing (earliestUpcomingShipmentID: undefined)
  const { isOrderPricingLoading, orderPricing } = useOrderPricing({
    shipmentID: earliestUpcomingShipmentID,
  });
  const prescription = useSelectorShared((state) => getPrescriptionByID(state, prescriptionID));
  const medication = useSelectorShared((state) => getMedicationForPrescription(state, prescription));
  const { cartAutoRefills } = useCartData();
  const pricing = orderPricing?.item_pricing[prescriptionID];

  if (!prescription || !pricing) {
    return null;
  }

  const itemPricingTotal = getItemPricingTotal(pricing);

  return (
    <Row
      spaceBetween
      gap="MD"
    >
      <MedContainer>
        <Body
          fontFamily="semibold"
          textSize="large"
        >
          {medication?.medicationName}
        </Body>
        <Row centerVertically>
          <Body>{prescription.dosage_form}</Body>
          {prescription.is_refrigerated ? (
            <Icon
              name="snowflake-small"
              type="disabled"
              accessibilityLabel="Medication requires refrigeration"
            />
          ) : null}
          {prescription.has_ancillary_items ? (
            <Icon
              name="syringe-small"
              type="disabled"
              accessibilityLabel="Medication comes with additional items"
            />
          ) : null}
        </Row>
        <Body>Qty: {formatQuantity(prescription)}</Body>
        <UserTag userIDs={[prescription.user_id]} />
        {prescription.auto_refill_eligible ? (
          <OrderTypeSelectionSection
            prescriptionID={prescription.id}
            orderTypeContext={ORDER_TYPE_CONTEXT.CHECKOUT}
            isEditable={false}
            initialAutoRefillEnabledValue={cartAutoRefills[prescription.id]}
          />
        ) : null}
        {includeNewItemTag ? (
          <Tag
            type="recommended"
            key="new-item"
            label="new item"
          />
        ) : null}
      </MedContainer>
      <Row right>
        {isOrderPricingLoading ? (
          <AltoSpinningLoader />
        ) : (
          <Body
            fontFamily="semibold"
            textSize="large"
          >
            {getPaymentString(itemPricingTotal)}
          </Body>
        )}
      </Row>
    </Row>
  );
};
